// components/finance/CashRunway.jsx - RESPONSIVE WITH THEME COLORS

import React, { useState, useEffect, useMemo } from 'react';
import { usePagePreferences } from '../../../hooks/useUserPreference';
import { ArrowDownIcon } from '@heroicons/react/24/outline';
import { formatCurrency, formatMonths } from '../../../utils';
import { Modal } from '../../common/Modal';
import { useCashRunway } from '../../../hooks/finance/useCashRunway';
import { GoalChart } from '../../common/GoalChart';
import { colorTheme } from '../../../theme/colors';

// ✅ USING THEME COLORS
const CASH_RUNWAY_CONFIG = {
  colors: {
    currentValue: colorTheme.green.main,      // #B0BC22
    remainingToGoal: colorTheme.grey.main,    // #DCDEEF
    surplus: colorTheme.yellow.main           // #FFDD00
  },
  text: {
    currentValue: 'Current Runway',
    remainingToGoal: 'Remaining To Target',
    surplus: 'Surplus',
    buttonName: 'Edit Target',
    goalTitle: 'Runway (Months)',
    goalDescription: ''
  }
};

// Cash Breakdown Content Component - RESPONSIVE
const CashBreakdownContent = ({ cashData }) => {
  const { accounts = [], bank_total, investment_total, total_cash } = cashData || {};

  if (!cashData || accounts.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No cash data available
      </div>
    );
  }

  const bankAccounts = accounts.filter(acc => acc.account_category === 'Bank');
  const investmentAccounts = accounts.filter(acc => acc.account_category === 'Investment');

  return (
    <div>
      {/* Summary Cards - RESPONSIVE GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-green-50 p-3 sm:p-4 rounded-lg">
          <div className="text-xs sm:text-sm font-medium mb-1" style={{ color: colorTheme.green.dark }}>
            Total Cash
          </div>
          <div className="text-xl sm:text-2xl font-bold" style={{ color: colorTheme.green.dark }}>
            {formatCurrency(total_cash)}
          </div>
        </div>
        <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
          <div className="text-xs sm:text-sm font-medium mb-1" style={{ color: colorTheme.brightblue.dark }}>
            Bank Accounts
          </div>
          <div className="text-xl sm:text-2xl font-bold" style={{ color: colorTheme.brightblue.dark }}>
            {formatCurrency(bank_total)}
          </div>
        </div>
        <div className="bg-purple-50 p-3 sm:p-4 rounded-lg">
          <div className="text-xs sm:text-sm font-medium mb-1" style={{ color: colorTheme.violet.dark }}>
            Investments
          </div>
          <div className="text-xl sm:text-2xl font-bold" style={{ color: colorTheme.violet.dark }}>
            {formatCurrency(investment_total)}
          </div>
        </div>
      </div>

      {/* Bank Accounts Table - RESPONSIVE */}
      {bankAccounts.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <h3 className="text-sm sm:text-md font-semibold text-gray-900 mb-2 sm:mb-3">Bank Accounts</h3>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium uppercase tracking-wider" style={{ color: colorTheme.brightblue.dark }}>
                      Account Name
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-medium uppercase tracking-wider hidden sm:table-cell" style={{ color: colorTheme.brightblue.dark }}>
                      Type
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-right sm:text-center text-[10px] sm:text-xs font-medium uppercase tracking-wider" style={{ color: colorTheme.brightblue.dark }}>
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bankAccounts.map((account, index) => (
                    <tr key={account.account_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm font-medium text-gray-900">
                        <div className="truncate max-w-[150px] sm:max-w-none">{account.account_name}</div>
                        <div className="sm:hidden text-[10px] text-gray-500 mt-1">{account.account_sub_type}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-center text-gray-600 hidden sm:table-cell">
                        {account.account_sub_type}
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-right sm:text-center font-semibold text-gray-900 whitespace-nowrap">
                        {formatCurrency(account.balance)}
                      </td>
                    </tr>
                  ))}
                  {/* Bank Total Row */}
                  <tr className="bg-blue-100">
                    <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm font-bold text-gray-900" colSpan="2">
                      Total Bank Accounts
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-right sm:text-center font-bold text-gray-900 whitespace-nowrap">
                      {formatCurrency(bank_total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Investment Accounts Table - RESPONSIVE */}
      {investmentAccounts.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <h3 className="text-sm sm:text-md font-semibold text-gray-900 mb-2 sm:mb-3">Investment Accounts</h3>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-purple-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium uppercase tracking-wider" style={{ color: colorTheme.violet.dark }}>
                      Account Name
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-medium uppercase tracking-wider hidden sm:table-cell" style={{ color: colorTheme.violet.dark }}>
                      Type
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-right sm:text-center text-[10px] sm:text-xs font-medium uppercase tracking-wider" style={{ color: colorTheme.violet.dark }}>
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {investmentAccounts.map((account, index) => (
                    <tr key={account.account_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm font-medium text-gray-900">
                        <div className="truncate max-w-[150px] sm:max-w-none">{account.account_name}</div>
                        <div className="sm:hidden text-[10px] text-gray-500 mt-1">{account.account_sub_type}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-center text-gray-600 hidden sm:table-cell">
                        {account.account_sub_type}
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-right sm:text-center font-semibold text-gray-900 whitespace-nowrap">
                        {formatCurrency(account.balance)}
                      </td>
                    </tr>
                  ))}
                  {/* Investment Total Row */}
                  <tr className="bg-purple-100">
                    <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm font-bold text-gray-900" colSpan="2">
                      Total Investments
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-right sm:text-center font-bold text-gray-900 whitespace-nowrap">
                      {formatCurrency(investment_total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Note */}
      <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-green-50 rounded-lg">
        <p className="text-[10px] sm:text-xs" style={{ color: colorTheme.green.dark }}>
          <strong>Note:</strong> Total cash includes both bank accounts and investment accounts.
        </p>
      </div>
    </div>
  );
};

// Expense Breakdown Content Component - RESPONSIVE
const ExpenseBreakdownContent = ({ expenseData }) => {
  const { monthly_breakdown = [], average_expenses, average_operating_expenses, average_cogs } = expenseData || {};

  if (!expenseData || monthly_breakdown.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8 text-sm">
        No expense data available
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards - RESPONSIVE GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
          <div className="text-xs sm:text-sm font-medium mb-1" style={{ color: colorTheme.brightblue.dark }}>
            Average Total
          </div>
          <div className="text-xl sm:text-2xl font-bold" style={{ color: colorTheme.brightblue.dark }}>
            {formatCurrency(average_expenses)}
          </div>
        </div>
        <div className="bg-purple-50 p-3 sm:p-4 rounded-lg">
          <div className="text-xs sm:text-sm font-medium mb-1" style={{ color: colorTheme.violet.dark }}>
            Average Operating
          </div>
          <div className="text-xl sm:text-2xl font-bold" style={{ color: colorTheme.violet.dark }}>
            {formatCurrency(average_operating_expenses)}
          </div>
        </div>
        <div className="bg-green-50 p-3 sm:p-4 rounded-lg">
          <div className="text-xs sm:text-sm font-medium mb-1" style={{ color: colorTheme.green.dark }}>
            Average COGS
          </div>
          <div className="text-xl sm:text-2xl font-bold" style={{ color: colorTheme.green.dark }}>
            {formatCurrency(average_cogs)}
          </div>
        </div>
      </div>

      {/* Monthly Table - RESPONSIVE */}
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                  Month
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-right sm:text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Operating
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-right sm:text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                  COGS
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-right sm:text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {monthly_breakdown.map((month, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm font-medium text-gray-900 sticky left-0 bg-inherit whitespace-nowrap">
                    {month.period_label}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-right sm:text-center text-gray-900 whitespace-nowrap">
                    {formatCurrency(month.operating_expenses)}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-right sm:text-center text-gray-900 whitespace-nowrap">
                    {formatCurrency(month.cost_of_goods_sold)}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-right sm:text-center font-semibold text-gray-900 whitespace-nowrap">
                    {formatCurrency(month.total_expenses)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Note */}
      <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-blue-50 rounded-lg">
        <p className="text-[10px] sm:text-xs" style={{ color: colorTheme.brightblue.dark }}>
          <strong>Note:</strong> Total expenses includes both operating expenses and cost of goods sold (COGS).
        </p>
      </div>
    </div>
  );
};

export const CashRunway = ({ 
  selectedYear = new Date().getFullYear(),
  goalMonths: initialGoalMonths = 4,
  onGoalChange,
  CONFIG = CASH_RUNWAY_CONFIG
}) => {
  const {
    currentCash,
    averageMonthlyExpense,
    cashData,
    expenseData,
    loading,
    error,
  } = useCashRunway(selectedYear);

  const [isEditing, setIsEditing] = useState(false);
  const [newGoal, setNewGoal] = useState(initialGoalMonths);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [preferences, setPreference, , prefsLoading, prefsError, prefsInitialized] = usePagePreferences(
    'finance_goals',
    { cash_runway_targets_by_year: {} }
  );

  const goalMonths = useMemo(() => {
    if (!prefsInitialized) return initialGoalMonths;
    const yearKey = String(selectedYear);
    return preferences.cash_runway_targets_by_year?.[yearKey] ?? initialGoalMonths;
  }, [prefsInitialized, selectedYear, preferences.cash_runway_targets_by_year, initialGoalMonths]);

  useEffect(() => {
    if (prefsInitialized) {
      setNewGoal(goalMonths);
    }
  }, [prefsInitialized, goalMonths]);

  const handleSaveGoal = async () => {
    setIsSaving(true);
    try {
      const yearKey = String(selectedYear);
      const updatedTargets = {
        ...(preferences.cash_runway_targets_by_year || {}),
        [yearKey]: newGoal
      };
      await setPreference('cash_runway_targets_by_year', updatedTargets);
      if (onGoalChange) onGoalChange(newGoal);
      setIsEditing(false);
    } catch (error) {
      console.error("❌ CashRunway: Error saving target:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setNewGoal(goalMonths);
    setIsEditing(false);
  };

  const currentRunwayMonths = averageMonthlyExpense > 0 
    ? currentCash / averageMonthlyExpense 
    : 0;
  
  const goal = isEditing ? newGoal : goalMonths;
  const targetCash = goal * averageMonthlyExpense;
  const percentage = goal > 0 ? (currentRunwayMonths / goal) * 100 : 0;
  const surplusMonths = Math.max(currentRunwayMonths - goal, 0);
  const surplusCash = Math.max(currentCash - targetCash, 0);
  const remainingCash = Math.max(targetCash - currentCash, 0);

  const isOverGoal = percentage > 100;
  const goalProportion = isOverGoal ? (goal / currentRunwayMonths) * 100 : percentage;
  const surplusProportion = isOverGoal ? (surplusMonths / currentRunwayMonths) * 100 : 0;
  
  const generateMonthMarkers = () => {
    const maxMonths = Math.ceil(Math.max(goal, currentRunwayMonths));
    const markers = [];
    const markerInterval = maxMonths <= 6 ? 1 : maxMonths <= 12 ? 2 : 3;
    
    for (let i = markerInterval; i <= maxMonths; i += markerInterval) {
      const isOverGoal = currentRunwayMonths > goal;
      const position = isOverGoal 
        ? (i / currentRunwayMonths) * 100 
        : (i / goal) * 100;
      
      if (position <= 100) {
        markers.push({ value: i, position });
      }
    }
    return markers;
  };

  if (loading && currentCash === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex justify-center items-center h-40">
          <div className="text-gray-500 text-sm">Loading cash runway...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Cash Breakdown Modal - RESPONSIVE */}
      <Modal
        isOpen={showCashModal}
        onClose={() => setShowCashModal(false)}
        title="Cash & Investment Breakdown"
        subtitle={cashData?.metadata?.as_of_date ? 
          `As of ${new Date(cashData.metadata.as_of_date).toLocaleDateString()}` 
          : undefined
        }
        size="lg"
        footer={
          <button 
            onClick={() => setShowCashModal(false)}
            className="px-3 sm:px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
          >
            Close
          </button>
        }
      >
        <CashBreakdownContent cashData={cashData} />
      </Modal>

      {/* Expense Breakdown Modal - RESPONSIVE */}
      <Modal
        isOpen={showExpenseModal}
        onClose={() => setShowExpenseModal(false)}
        title="Monthly Expense Breakdown"
        subtitle={expenseData?.period ? 
          `${expenseData.period.start_date} to ${expenseData.period.end_date} (${expenseData.monthly_breakdown?.length || 0} months)` 
          : undefined
        }
        size="lg"
        footer={
          <button 
            onClick={() => setShowExpenseModal(false)}
            className="px-3 sm:px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
          >
            Close
          </button>
        }
      >
        <ExpenseBreakdownContent expenseData={expenseData} />
      </Modal>
      
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        {error && (
          <div className="mx-3 sm:mx-6 mt-3 sm:mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs sm:text-sm text-yellow-800">{error}</p>
          </div>
        )}

        <div className="p-3 sm:p-6 bg-gradient-to-r from-white to-gray-50 rounded-t-lg">
          <GoalChart
            currentValue={currentCash}
            goalValue={targetCash}
            formatValue={formatCurrency}
            config={CONFIG}
            isEditing={isEditing}
            onEditClick={() => setIsEditing(true)}
            onSave={handleSaveGoal}
            onCancel={handleCancelEdit}
            editValue={newGoal}
            onEditValueChange={(val) => setNewGoal(Number(val))}
            isSaving={isSaving}
            inputType="number"
            inputPlaceholder="Months"
            inputProps={{ min: 0, step: 0.5 }}
            markers={generateMonthMarkers()}
            formatMarker={(val) => val.toString()}
          />
        </div>

        <div className="px-3 sm:px-6 pt-0 pb-3 sm:pb-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
            {CONFIG.text.goalTitle} ({selectedYear})
          </h3>
          {CONFIG.text.goalDescription && (
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
              {CONFIG.text.goalDescription}
            </p>
          )}
          
          {/* Stats Grid - RESPONSIVE */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-3 sm:pt-4 border-t border-gray-200">
            <div 
              className="text-center cursor-pointer hover:bg-gray-50 rounded-lg p-3 transition-colors"
              onClick={() => setShowCashModal(true)}
            >
              <div className="text-lg sm:text-xl font-semibold text-gray-900">
                {formatCurrency(currentCash)}
              </div>
              <div className="text-xs text-gray-500 mt-1">Current Cash</div>
              
              <div className="mt-2 text-[10px] sm:text-xs text-gray-400 flex items-center justify-center gap-2">
                <span className="hidden sm:inline">Click to view details</span>
                <span className="sm:hidden">View details</span>
                <div className="group flex items-center justify-center h-5 w-5 sm:h-6 sm:w-6 rounded-full border-2 border-gray-400 hover:border-blue-500 hover:bg-blue-500 hover:scale-110 transition-all">
                  <div className="text-gray-400 group-hover:text-white transition-colors text-sm sm:text-lg leading-none">↗</div>
                </div>
              </div>
            </div>
            
            <div 
              className="text-center cursor-pointer hover:bg-gray-50 rounded-lg p-3 transition-colors"
              onClick={() => setShowExpenseModal(true)}
            >
              <div className="text-lg sm:text-xl font-semibold text-gray-900">
                {formatCurrency(averageMonthlyExpense)}
              </div>
              <div className="text-xs text-gray-500 mt-1">Avg Monthly Expense</div>
              
              <div className="mt-2 text-[10px] sm:text-xs text-gray-400 flex items-center justify-center gap-2">
                <span className="hidden sm:inline">Click to view details</span>
                <span className="sm:hidden">View details</span>
                <div className="group flex items-center justify-center h-5 w-5 sm:h-6 sm:w-6 rounded-full border-2 border-gray-400 hover:border-blue-500 hover:bg-blue-500 hover:scale-110 transition-all">
                  <div className="text-gray-400 group-hover:text-white transition-colors text-sm sm:text-lg leading-none">↗</div>
                </div>
              </div>
            </div>
            
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-lg sm:text-xl font-semibold text-gray-900">
                {formatMonths(currentRunwayMonths)} mo
              </div>
              <div className="text-xs text-gray-500 mt-1">Months of Runway</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};